import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Loader2, Plus, ArrowLeft, Save, Send, FlaskConical, Pause, Play, Copy, Trash2, CalendarClock, AlertTriangle, Undo2, Mail,
} from 'lucide-react';
import { Modal, Badge, btnPrimary, btnGhost, inputCls } from '../ChatAssistant/ui';
import { mailApi, fmtDate, fmtNum, pct, STARTER_HTML } from './api';
import { HtmlEditor, InboxPreview } from './Editor';

const STATUS = {
  draft: { tone: 'gray', label: 'Draft' },
  scheduled: { tone: 'indigo', label: 'Scheduled' },
  sending: { tone: 'amber', label: 'Sending' },
  paused: { tone: 'amber', label: 'Paused' },
  sent: { tone: 'green', label: 'Sent' },
  failed: { tone: 'red', label: 'Failed' },
};

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Taslak editörü ───────────────────────────────────────────────────────────
function CampaignEditor({ brandId, campaign, onChange, onBack }) {
  const [c, setC] = useState(campaign);
  const [saved, setSaved] = useState(campaign);
  const [senders, setSenders] = useState(null);
  const [lists, setLists] = useState([]);
  const [audience, setAudience] = useState(campaign.audience ?? null);
  const [busy, setBusy] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [mode, setMode] = useState('now');

  const [localPart, setLocalPart] = useState((campaign.fromEmail || 'hello@').split('@')[0]);
  const [domain, setDomain] = useState((campaign.fromEmail || '').split('@')[1] || '');

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([mailApi(brandId, '/senders'), mailApi(brandId, '/lists')]);
        setSenders(s.domains);
        setLists(l.lists);
        if (!domain && s.domains[0]) setDomain(s.domains[0]);
      } catch (e) { toast.error(e.message); }
    })();
  }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fromEmail = domain ? `${localPart.trim()}@${domain}` : '';
  useEffect(() => { setC((x) => ((x.fromEmail || '') === fromEmail ? x : { ...x, fromEmail })); }, [fromEmail]);

  useEffect(() => {
    if (!c.listIds.length) { setAudience(0); return; }
    const t = setTimeout(async () => {
      try { setAudience((await mailApi(brandId, '/audience', { query: { listIds: c.listIds.join(',') } })).count); } catch (e) { /* sessiz */ }
    }, 200);
    return () => clearTimeout(t);
  }, [brandId, c.listIds]);

  const fields = ['name', 'subject', 'preheader', 'fromName', 'fromEmail', 'replyTo', 'html', 'listIds'];
  const dirty = fields.some((k) => JSON.stringify(c[k]) !== JSON.stringify(saved[k]));

  const save = async (quiet) => {
    if (!dirty) return saved;
    const body = Object.fromEntries(fields.map((k) => [k, c[k]]));
    const r = await mailApi(brandId, `/campaigns/${c.id}`, { method: 'PATCH', body });
    setSaved(r.campaign);
    setC((x) => ({ ...x, ...r.campaign }));
    if (!quiet) toast.success('Saved');
    return r.campaign;
  };

  const run = (key, fn) => async () => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const sendTest = run('test', async () => {
    await save(true);
    const r = await mailApi(brandId, `/campaigns/${c.id}/test`, { method: 'POST', body: { emails: testTo } });
    const bad = r.results.filter((x) => !x.ok);
    if (bad.length) toast.error(`Failed: ${bad.map((b) => `${b.email} (${b.error})`).join(', ')}`, { duration: 8000 });
    else { toast.success(`Test sent to ${r.results.map((x) => x.email).join(', ')}`); setTestOpen(false); }
  });

  const send = run('send', async () => {
    await save(true);
    const body = mode === 'later' ? { scheduledAt: new Date(scheduleAt).toISOString() } : {};
    const r = await mailApi(brandId, `/campaigns/${c.id}/send`, { method: 'POST', body });
    toast.success(mode === 'later' ? `Scheduled for ${fmtDate(r.campaign.scheduledAt)}` : `Sending to ${fmtNum(r.campaign.audience)} contacts`);
    setSendOpen(false);
    onChange(r.campaign.id);
  });

  const toggleList = (id) => setC((x) => ({ ...x, listIds: x.listIds.includes(id) ? x.listIds.filter((i) => i !== id) : [...x.listIds, id] }));

  const missing = [
    !c.subject?.trim() && 'subject',
    !c.html?.trim() && 'content',
    !fromEmail && 'from address',
    !c.listIds.length && 'audience',
  ].filter(Boolean);

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button className={btnGhost} onClick={() => (!dirty || window.confirm('Discard unsaved changes?')) && onBack()}><ArrowLeft />Campaigns</button>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">{dirty ? 'Unsaved changes' : 'Saved'}</span>
          <button className={btnGhost} onClick={run('save', () => save(false))} disabled={!dirty || !!busy}>{busy === 'save' ? <Loader2 className="animate-spin" /> : <Save />}Save</button>
          <button className={btnGhost} onClick={() => setTestOpen(true)} disabled={!!busy}><FlaskConical />Send test</button>
          <button className={btnPrimary} onClick={() => setSendOpen(true)} disabled={!!busy || missing.length > 0} title={missing.length ? `Missing: ${missing.join(', ')}` : ''}><Send />Send…</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
        <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Campaign name <span className="text-gray-400 font-normal">(internal)</span></label>
            <input className={inputCls} value={c.name || ''} onChange={(e) => setC({ ...c, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From name</label>
              <input className={inputCls} value={c.fromName || ''} onChange={(e) => setC({ ...c, fromName: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From address</label>
              {senders === null ? <div className="text-xs text-gray-400 py-1.5">Loading…</div> : senders.length === 0 ? (
                <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">No verified sending domain. Add one under Settings → Sending domains.</div>
              ) : (
                <div className="flex items-center gap-1">
                  <input className={`${inputCls.replace('w-full ', '')} w-24 shrink-0`} value={localPart} onChange={(e) => setLocalPart(e.target.value.replace(/[@\s]/g, ''))} />
                  <span className="text-gray-400 text-sm">@</span>
                  <select className={`${inputCls.replace('w-full ', '')} flex-1 min-w-0`} value={domain} onChange={(e) => setDomain(e.target.value)}>
                    {senders.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Reply-to <span className="text-gray-400 font-normal">(optional — where replies go)</span></label>
            <input className={inputCls} placeholder="e.g. destek@brand.com" value={c.replyTo || ''} onChange={(e) => setC({ ...c, replyTo: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <input className={inputCls} value={c.subject || ''} onChange={(e) => setC({ ...c, subject: e.target.value })} placeholder="{{first_name|Merhaba}}, yeni sezon geldi" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Preview text <span className="text-gray-400 font-normal">(shown after the subject in the inbox)</span></label>
            <input className={inputCls} value={c.preheader || ''} onChange={(e) => setC({ ...c, preheader: e.target.value })} />
          </div>
        </section>

        <aside className="space-y-4">
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Audience</h3>
            {lists.length === 0 ? (
              <p className="text-xs text-gray-500">No lists yet. Import contacts first.</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {lists.map((l) => (
                  <label key={l.id} className="flex items-center justify-between gap-2 text-xs text-gray-700">
                    <span className="flex items-center gap-1.5"><input type="checkbox" checked={c.listIds.includes(Number(l.id))} onChange={() => toggleList(Number(l.id))} />{l.name}</span>
                    <span className="text-gray-400">{fmtNum(l.subscribed)}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
              <b className="text-gray-900 text-sm">{audience === null ? '…' : fmtNum(audience)}</b> recipients
              <div className="text-[11px] text-gray-400 mt-0.5">Subscribed only, duplicates across lists counted once.</div>
            </div>
          </section>
          <section>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Inbox preview</h3>
            <InboxPreview fromName={c.fromName} subject={c.subject} preheader={c.preheader} />
          </section>
        </aside>
      </div>

      <HtmlEditor value={c.html || ''} onChange={(html) => setC({ ...c, html })} />
      <p className="text-[11px] text-gray-500">
        An unsubscribe footer is added automatically unless the HTML contains <code>{'{{unsubscribe_url}}'}</code>. Links are tracked for click reporting; a one-click unsubscribe header is added for Gmail and Yahoo.
      </p>

      {testOpen && (
        <Modal title="Send test email" onClose={() => !busy && setTestOpen(false)} footer={<>
          <button className={btnGhost} onClick={() => setTestOpen(false)} disabled={!!busy}>Cancel</button>
          <button className={btnPrimary} onClick={sendTest} disabled={!!busy || !testTo.trim()}>{busy === 'test' && <Loader2 className="animate-spin" />}Send test</button>
        </>}>
          <label className="block text-xs font-medium text-gray-700 mb-1">Send to (up to 5, comma separated)</label>
          <input className={inputCls} autoFocus value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@company.com" />
          <p className="text-[11px] text-gray-500 mt-2">The subject gets a [TEST] prefix. Merge tags use the first contact in the selected lists. Tests aren't counted in reports.</p>
        </Modal>
      )}

      {sendOpen && (
        <Modal title="Send campaign" onClose={() => !busy && setSendOpen(false)} footer={<>
          <button className={btnGhost} onClick={() => setSendOpen(false)} disabled={!!busy}>Cancel</button>
          <button className={btnPrimary} onClick={send} disabled={!!busy || (mode === 'later' && !scheduleAt)}>
            {busy === 'send' && <Loader2 className="animate-spin" />}{mode === 'later' ? 'Schedule' : `Send to ${fmtNum(audience)} now`}
          </button>
        </>}>
          <div className="space-y-3">
            <InboxPreview fromName={c.fromName} subject={c.subject} preheader={c.preheader} />
            <dl className="text-xs grid grid-cols-[90px_1fr] gap-y-1">
              <dt className="text-gray-500">From</dt><dd className="text-gray-900">{c.fromName} &lt;{fromEmail}&gt;</dd>
              <dt className="text-gray-500">Recipients</dt><dd className="text-gray-900">{fmtNum(audience)}</dd>
              <dt className="text-gray-500">Duration</dt><dd className="text-gray-900">~{Math.max(1, Math.ceil((audience || 0) / 600))} min at 10 emails/sec</dd>
            </dl>
            <div className="flex gap-4 text-xs">
              <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'now'} onChange={() => setMode('now')} />Send now</label>
              <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'later'} onChange={() => setMode('later')} />Schedule</label>
            </div>
            {mode === 'later' && <input type="datetime-local" className={inputCls} value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />}
            <p className="text-[11px] text-gray-500">Contacts who unsubscribe before their email goes out are skipped automatically. You can pause while it's sending.</p>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Rapor (gönderilmiş / gönderiliyor / zamanlanmış) ─────────────────────────
function CampaignReport({ brandId, campaign, onChange, onBack, onDuplicate }) {
  const c = campaign;
  const s = c.stats;
  const [busy, setBusy] = useState(null);
  const act = (key, path) => async () => {
    setBusy(key);
    try { await mailApi(brandId, `/campaigns/${c.id}/${path}`, { method: 'POST' }); onChange(c.id); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const done = s.sent + s.failed + s.skipped;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button className={btnGhost} onClick={onBack}><ArrowLeft />Campaigns</button>
        <div className="flex gap-2">
          {c.status === 'sending' && <button className={btnGhost} onClick={act('pause', 'pause')} disabled={!!busy}><Pause />Pause</button>}
          {c.status === 'paused' && <button className={btnPrimary} onClick={act('resume', 'resume')} disabled={!!busy}><Play />Resume</button>}
          {c.status === 'scheduled' && <button className={btnGhost} onClick={act('unschedule', 'unschedule')} disabled={!!busy}><Undo2 />Back to draft</button>}
          <button className={btnGhost} onClick={() => onDuplicate(c)}><Copy />Duplicate</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-gray-900">{c.name}</h2>
          <Badge tone={STATUS[c.status]?.tone}>{STATUS[c.status]?.label}</Badge>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          “{c.subject}” · {c.fromName} &lt;{c.fromEmail}&gt;
          {c.status === 'scheduled' && <> · goes out {fmtDate(c.scheduledAt)}</>}
          {c.startedAt && <> · started {fmtDate(c.startedAt)}</>}
          {c.finishedAt && <> · finished {fmtDate(c.finishedAt)}</>}
        </p>
        {c.lastError && <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 mt-2 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />{c.lastError}</p>}
        {['sending', 'paused'].includes(c.status) && s.total > 0 && (
          <div className="mt-3">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-600 transition-all" style={{ width: `${(done / s.total) * 100}%` }} /></div>
            <div className="text-[11px] text-gray-500 mt-1">{fmtNum(done)} / {fmtNum(s.total)} processed</div>
          </div>
        )}
      </div>

      {c.status !== 'scheduled' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Sent" value={fmtNum(s.sent)} sub={s.failed || s.skipped ? `${fmtNum(s.failed)} failed · ${fmtNum(s.skipped)} skipped` : `of ${fmtNum(s.total)}`} />
          <Stat label="Opened" value={pct(s.opened, s.sent)} sub={`${fmtNum(s.opened)} people`} />
          <Stat label="Clicked" value={pct(s.clicked, s.sent)} sub={`${fmtNum(s.clicked)} people`} />
          <Stat label="Click-to-open" value={pct(s.clicked, s.opened)} />
          <Stat label="Unsubscribed" value={pct(s.unsubscribed, s.sent)} sub={`${fmtNum(s.unsubscribed)} people`} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white rounded-xl border border-gray-200">
          <h3 className="px-4 py-2.5 border-b border-gray-100 text-xs font-semibold text-gray-700">Top links</h3>
          {c.links?.length ? (
            <table className="w-full text-xs"><tbody>
              {c.links.map((l) => (
                <tr key={l.url} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2 text-gray-700 truncate max-w-[360px]" title={l.url}>{l.url}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{fmtNum(l.clicks)}</td>
                </tr>
              ))}
            </tbody></table>
          ) : <p className="px-4 py-3 text-xs text-gray-500">No clicks yet.</p>}
        </section>
        <section className="bg-white rounded-xl border border-gray-200">
          <h3 className="px-4 py-2.5 border-b border-gray-100 text-xs font-semibold text-gray-700">Recipients <span className="font-normal text-gray-400">(latest 100, failures first)</span></h3>
          <div className="max-h-80 overflow-y-auto">
            {c.recipients?.length ? (
              <table className="w-full text-xs"><tbody>
                {c.recipients.map((r) => (
                  <tr key={r.email} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-1.5 text-gray-700">{r.email}</td>
                    <td className="px-2 py-1.5">
                      <Badge tone={r.status === 'sent' ? 'green' : r.status === 'failed' ? 'red' : 'gray'}>{r.status}</Badge>
                      {r.opened_at && <span className="ml-1 text-[10px] text-indigo-600">opened</span>}
                      {r.clicked_at && <span className="ml-1 text-[10px] text-indigo-600">clicked</span>}
                      {r.unsubscribed_at && <span className="ml-1 text-[10px] text-gray-500">unsubscribed</span>}
                    </td>
                    <td className="px-4 py-1.5 text-gray-400 truncate max-w-[180px]" title={r.error || ''}>{r.error || ''}</td>
                  </tr>
                ))}
              </tbody></table>
            ) : <p className="px-4 py-3 text-xs text-gray-500">Recipients appear once sending starts.</p>}
          </div>
        </section>
      </div>

      <section>
        <h3 className="text-xs font-semibold text-gray-700 mb-2">Content</h3>
        <HtmlEditor value={c.html || ''} readOnly height={480} />
      </section>
    </div>
  );
}

// ── Liste ───────────────────────────────────────────────────────────────────
function NewCampaignModal({ brandId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { mailApi(brandId, '/templates').then((r) => setTemplates(r.templates)).catch(() => setTemplates([])); }, [brandId]);
  const create = async () => {
    setBusy(true);
    try {
      const body = templateId ? { name, templateId } : { name, html: STARTER_HTML };
      onCreated((await mailApi(brandId, '/campaigns', { method: 'POST', body })).campaign);
    } catch (e) { toast.error(e.message); setBusy(false); }
  };
  return (
    <Modal title="New campaign" onClose={() => !busy && onClose()} footer={<>
      <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
      <button className={btnPrimary} onClick={create} disabled={busy || !name.trim()}>{busy && <Loader2 className="animate-spin" />}Create</button>
    </>}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Campaign name</label>
          <input className={inputCls} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Autumn launch — October" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Start from</label>
          <select className={inputCls} value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={templates === null}>
            <option value="">Basic layout</option>
            {(templates || []).map((t) => <option key={t.id} value={t.id}>Template: {t.name}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}

export default function CampaignsTab({ brandId }) {
  const [campaigns, setCampaigns] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [current, setCurrent] = useState(null);
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { setCampaigns((await mailApi(brandId, '/campaigns')).campaigns); }
    catch (e) { toast.error(e.message); setCampaigns([]); }
  }, [brandId]);

  const loadOne = useCallback(async (id) => {
    try { setCurrent((await mailApi(brandId, `/campaigns/${id}`)).campaign); }
    catch (e) { toast.error(e.message); setOpenId(null); }
  }, [brandId]);

  useEffect(() => { setCampaigns(null); setOpenId(null); setCurrent(null); load(); }, [load]);
  useEffect(() => { if (openId) { setCurrent(null); loadOne(openId); } }, [openId, loadOne]);

  // Gönderim/zamanlama sürerken 5 sn'de bir tazele
  useEffect(() => {
    clearInterval(pollRef.current);
    const live = (s) => ['sending', 'scheduled'].includes(s);
    if ((openId && current && live(current.status)) || (!openId && campaigns?.some((c) => live(c.status)))) {
      pollRef.current = setInterval(() => (openId ? loadOne(openId) : load()), 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [openId, current, campaigns, load, loadOne]);

  const back = () => { setOpenId(null); setCurrent(null); load(); };
  const duplicate = async (c) => {
    try { const r = await mailApi(brandId, `/campaigns/${c.id}/duplicate`, { method: 'POST' }); setOpenId(r.campaign.id); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await mailApi(brandId, `/campaigns/${c.id}`, { method: 'DELETE' }); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (openId) {
    if (!current) return <div className="flex items-center justify-center py-16 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>;
    return current.status === 'draft'
      ? <CampaignEditor key={current.id} brandId={brandId} campaign={current} onBack={back} onChange={(id) => loadOne(id)} />
      : <CampaignReport brandId={brandId} campaign={current} onBack={back} onChange={(id) => loadOne(id)} onDuplicate={duplicate} />;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900">Campaigns</h2>
          <p className="text-xs text-gray-500 mt-0.5">One-off emails to one or more lists, sent from the brand's verified domain.</p>
        </div>
        <button className={btnPrimary} onClick={() => setCreating(true)}><Plus />New campaign</button>
      </div>

      {campaigns === null ? (
        <div className="flex items-center justify-center py-16 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 py-12 text-center">
          <Mail className="w-6 h-6 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-700 font-medium">No campaigns yet</p>
          <p className="text-xs text-gray-500 mt-1">You need a verified sending domain and a list of contacts to send.</p>
        </div>
      ) : (
        <section className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                <th className="px-4 py-2 font-semibold">Campaign</th>
                <th className="px-2 py-2 font-semibold">Status</th>
                <th className="px-2 py-2 font-semibold text-right">Sent</th>
                <th className="px-2 py-2 font-semibold text-right">Opened</th>
                <th className="px-2 py-2 font-semibold text-right">Clicked</th>
                <th className="px-2 py-2 font-semibold text-right">Unsub.</th>
                <th className="px-2 py-2 font-semibold">Date</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="group border-b border-gray-50 last:border-0 hover:bg-gray-50/60 cursor-pointer" onClick={() => setOpenId(c.id)}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900">{c.name}</div>
                    <div className="text-[11px] text-gray-500 truncate max-w-[320px]">{c.subject || 'No subject'}</div>
                  </td>
                  <td className="px-2 py-2.5"><Badge tone={STATUS[c.status]?.tone}>{STATUS[c.status]?.label}</Badge></td>
                  <td className="px-2 py-2.5 text-right text-gray-900">{c.stats.total ? fmtNum(c.stats.sent) : '—'}</td>
                  <td className="px-2 py-2.5 text-right text-gray-700">{c.stats.sent ? pct(c.stats.opened, c.stats.sent) : '—'}</td>
                  <td className="px-2 py-2.5 text-right text-gray-700">{c.stats.sent ? pct(c.stats.clicked, c.stats.sent) : '—'}</td>
                  <td className="px-2 py-2.5 text-right text-gray-700">{c.stats.sent ? pct(c.stats.unsubscribed, c.stats.sent) : '—'}</td>
                  <td className="px-2 py-2.5 text-gray-500 whitespace-nowrap">
                    {c.status === 'scheduled' ? <span className="inline-flex items-center gap-1 text-indigo-700"><CalendarClock className="w-3 h-3" />{fmtDate(c.scheduledAt)}</span> : fmtDate(c.finishedAt || c.startedAt || c.updatedAt)}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button className="text-gray-300 hover:text-gray-700 opacity-0 group-hover:opacity-100 mr-2" title="Duplicate" onClick={(e) => { e.stopPropagation(); duplicate(c); }}><Copy className="w-3.5 h-3.5" /></button>
                    {['draft', 'scheduled'].includes(c.status) && (
                      <button className="text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100" title="Delete" onClick={(e) => { e.stopPropagation(); remove(c); }}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {creating && <NewCampaignModal brandId={brandId} onClose={() => setCreating(false)} onCreated={(c) => { setCreating(false); setOpenId(c.id); }} />}
    </div>
  );
}
